/**
 * 서버와 말하는 자리. **여기 없는 경로는 서버에도 없다.**
 *
 * 2026-08-26 에 칸반만 남기고 걷어냈다 — 배포·에이전트·문서·감사·권한·
 * GitHub 연동은 커밋 `69a2dcb` 에 있다.
 */

/** 상태(칸). 화면에서는 「상태」로 부른다. */
export type TicketCol = 'issue' | 'todo' | 'in_progress' | 'done';
/**
 * 티켓 유형. **만들 때 정하고 안 바뀐다.**
 *
 * ⚠ 칸 이름 `issue` 와 글자가 같지만 **다른 축이다** — 유형이 `todo` 인
 * 티켓도 접수함인 `issue` 칸에 설 수 있다. 둘을 한 값으로 합치지 마라.
 */
export type TicketKind = 'todo' | 'issue';
/** 작업 유형. 서버 칸 이름은 `type` 이다. */
export type TicketType = 'bug' | 'feature' | 'task';
export type TicketPriority = 'urgent' | 'high' | 'medium' | 'low';
/** 디바이스·환경은 **이슈에만** 있다. */
export type TicketDevice = 'ios' | 'android' | 'desktop' | 'mobile_web';
export type TicketEnvironment = 'local' | 'staging' | 'production';

/**
 * 역할 셋 — **직무이지 계층이 아니다.** 셋 사이에 위아래가 없고, 이것으로
 * 할 수 있는 일이 갈리지 않는다.
 *
 * ⚠ **무엇을 할 수 있는지는 `is_admin` 이 정한다**(다른 축이다). 개발이어도
 * 관리자가 아니면 프로젝트를 못 만들고, 기획인 관리자는 만든다.
 */
export type Role = 'planner' | 'qa' | 'developer';

/** 화면이 쓰는 역할 이름. **여기 하나가 주인이다** — 화면마다 적으면 갈린다. */
export const ROLE_LABEL: Record<Role, string> = {
  planner: '기획',
  qa: 'QA',
  developer: '개발',
};

export type User = {
  id: number;
  email: string;
  /** 로그인 제공자가 준 실명. 화면에서 못 고친다. */
  name: string;
  /** 사람이 고른 이름. `null` 이 「안 정했다」다. */
  nickname: string | null;
  /** 화면이 실제로 보여 줄 이름. **서버가 정한다.** */
  display_name: string;
  /** 직무. 화면에 「누구로 보이는가」다. */
  role: Role;
  /** 관리자인가. **참이면 아래 목록과 무관하게 전부 통과한다.** */
  is_admin: boolean;
  /**
   * 내가 실제로 할 수 있는 것. **관리자면 전부 실려 온다.**
   *
   * ⚠ 화면은 이것으로 **메뉴를 숨길 뿐이다.** 막는 것은 서버다 — 숨긴
   * 화면의 API 는 그대로 열려 있고, 주소를 아는 사람은 그냥 친다.
   */
  permissions: string[];
  /** 프로필 사진 주소. `null` 이면 화면이 이니셜을 그린다. */
  avatar_url: string | null;
};

/* ── 보안 접속 승인 (2026-09-16) ────────────────────────────────────
   백오피스가 `wl.viewster.io` 를 이어받는다. 자세한 것은 서버의
   `app/access_gate.py` 머리말. */

export type AccessState = {
  /** 나에게 살아 있는 승인이 있나. */
  on: boolean;
  /** 언제 저절로 꺼지나. ISO 문자열. */
  expires_at: string | null;
  /** 지금 열려 있는 주소들. 집과 사무실을 따로 켤 수 있다. */
  ips: string[];
  /**
   * **실제로 WAF 까지 닿나.** 거짓이면 표에만 남는다(서버 스위치 꺼짐).
   *
   * ⚠ 이때 화면이 「열렸다」고만 적으면 **꺼진 스위치가 켜진 것처럼 보인다.**
   */
  enforced: boolean;
  /** 한 번 켜면 몇 시간인가. */
  hours: number;
  /** 지금 이 브라우저가 나가는 주소. 못 읽으면 `null`. */
  client_ip: string | null;
  active_devices?: number;
};

export function getAccess(): Promise<AccessState> {
  return request<AccessState>('/access');
}

export function openAccess(): Promise<AccessState> {
  return request<AccessState>('/access', { method: 'POST' });
}

export function closeAccess(allDevices = false): Promise<AccessState> {
  return request<AccessState>(allDevices ? '/access?all_devices=true' : '/access', { method: 'DELETE' });
}

/** 이 권한을 가졌나. **화면이 `is_admin` 을 다시 보지 않는다** — 서버가
 *  관리자에게는 목록을 전부 실어 보낸다. */
export function can(user: User, key: string): boolean {
  return user.permissions.includes(key);
}

export type Member = {
  id: number;
  email: string;
  display_name: string;
  role: Role;
  /** 관리자인가. **보여 주기만 한다** — 이 API 로 못 바꾼다. */
  is_admin: boolean;
  /**
   * 계정이 켜져 있나. 끄면 못 들어온다.
   *
   * **끈 사람도 목록에 온다** — 안 그러면 화면에서 사라져 되살릴 길이 없다.
   */
  is_active: boolean;
  /** 표에 실제로 저장된 키. **관리자여도 저장된 것만 온다.** */
  permissions: string[];
};

/** 체크박스 하나를 그리는 데 필요한 것. **서버가 목록을 내려준다.** */
export type PermissionDef = {
  key: string;
  label: string;
  /** `ticket` · `label` · `project` · `user`. 화면이 이것으로 줄을 가른다. */
  group: string;
  group_label: string;
};

export type MemberList = {
  users: Member[];
  /** 화면에서 줄 수 있는 역할 — 셋 다다. */
  roles: Role[];
  /** 줄 수 있는 권한 전부. **화면이 목록을 지어내지 않는다.** */
  permissions: PermissionDef[];
};

export type UserBrief = {
  id: number;
  display_name: string;
  /** 직무. 작업자를 고를 때 **누가 무엇을 하는 사람인지** 보여 준다. */
  role: Role;
  /** 아바타 글자 하나. **서버가 자른다** — 화면마다 다르게 자르면 안 된다. */
  initial: string;
  /**
   * 프로필 사진 주소. `null` 이면 이니셜을 그린다.
   *
   * **바이트가 아니라 주소다** — 브라우저가 캐시해 한 번만 받아 간다.
   * 주소 뒤에 바뀐 때가 붙어 있어 사진을 바꾸면 주소가 달라진다.
   */
  avatar_url: string | null;
};

export type Project = { id: number; key: string; name: string };
export type ProjectList = { projects: Project[] };

export type Label = { id: number; name: string; color: LabelColor };
/**
 * 라벨 색. **`styles.css` 의 상태색 토큰과 같은 여섯**이다 —
 * 늘리려면 테마 일곱 곳을 함께 고쳐야 한다.
 */
export type LabelColor = 'gray' | 'blue' | 'amber' | 'green' | 'red' | 'violet';

export type LabelList = { labels: Label[]; colors: LabelColor[] };

export type TicketCard = {
  id: number;
  /** 이 티켓이 사는 프로젝트. 상세 화면이 라벨 목록을 부를 때 쓴다. */
  project_id: number;
  /** `PLUS-118`. **서버가 조립한다.** */
  key: string;
  title: string;
  col: TicketCol;
  position: number;
  kind: TicketKind;
  /** 아래 넷은 `null` 이 「안 정했다」다 — 그때 화면은 **아무것도 안 그린다.** */
  type: TicketType | null;
  priority: TicketPriority | null;
  /** 작업자. **역할로 안 좁힌다**(2026-08-26) — 셋 다 될 수 있다. */
  assignee: UserBrief | null;
  due_date: string | null;
  labels: Label[];
  /** 생성자. */
  reporter: UserBrief | null;

  /** 이슈에만 있다. 유형이 `todo` 면 언제나 `null` 이다. */
  device: TicketDevice | null;
  environment: TicketEnvironment | null;

  /**
   * 미뤄 둔 시각. **`null` 이 「안 미뤘다」다**(2026-09-02).
   * 칸과 다른 축이라 어느 칸에 있든 값이 설 수 있다.
   */
  held_at: string | null;
  hold_reason: string | null;

  /** 일자 셋. **서버가 채운다** — 화면이 보내는 값이 아니다. */
  started_at: string | null;
  finished_at: string | null;
  /** 며칠 걸릴 예정인가. 「작업 중」으로 옮기며 적는다. */
  estimate_days: number | null;
  created_at: string;
  updated_at: string;
};

export type TicketDetail = TicketCard & {
  body: string;
};

export type TicketList = {
  tickets: TicketCard[];
  /** **필터를 건 뒤의 수**다. 카드와 배지가 어긋나면 안 된다. */
  counts: Record<TicketCol, number>;
};

/** 본문 양식의 절 하나. **정본은 서버**(`app/ticket_templates.py`)다. */
export type TemplateSection = {
  heading: string;
  /** 그 절에 무엇을 적나. placeholder 로 쓴다. */
  hint: string;
};

/**
 * 챗봇이 던지는 고정 질문 하나.
 *
 * ⚠ **묻는 차례가 본문 절 차례와 다르다**(2026-09-16) — 사람은 무슨 일이
 * 났는지부터 말하는데 본문은 재현 절차가 먼저 서야 읽힌다. `heading` 이
 * 둘을 잇는다.
 *
 * 화면이 순서대로 하나씩 묻고 **셋을 다 받은 뒤에야 서버를 부른다** —
 * 질문마다 모델을 부르면 값이 세 배가 되고 매번 다른 것을 묻는다.
 */
export type TicketQuestion = {
  heading: string;
  text: string;
};

export type TicketMeta = {
  columns: TicketCol[];
  kinds: TicketKind[];
  types: TicketType[];
  priorities: TicketPriority[];
  devices: TicketDevice[];
  environments: TicketEnvironment[];
  label_colors: LabelColor[];
  /** 유형마다 하나. 챗봇의 초안과 「양식 넣기」가 같은 절 이름을 쓴다. */
  templates: Record<TicketKind, TemplateSection[]>;
  /** 챗봇이 순서대로 던질 고정 질문. **화면이 목록을 지어내지 않는다.** */
  questions: Record<TicketKind, TicketQuestion[]>;
};

/* ── 챗봇 초안 (2026-09-16) ─────────────────────────────────────────── */

/**
 * 물음 하나와 그 답.
 *
 * ⚠ **물음 글자를 화면이 보낸다.** 여는 질문 말고는 **AI 가 만든 것**이라
 * 서버에 없다 — 안 보내면 모델이 답만 보고 맥락을 지어낸다.
 */
export type DraftAnswer = {
  /** 이 답이 채울 본문 절. */
  heading: string;
  question: string;
  text: string;
};

/** AI 가 낸 물음. **`heading` 이 그 답이 갈 절을 정한다.** */
export type DraftQuestion = {
  heading: string;
  text: string;
  /** 단추로 그릴 선택지. 비면 자유 답만. */
  options: string[];
};

/**
 * 모델이 세운 초안. **폼의 시작값이지 저장값이 아니다.**
 *
 * ⚠ **유형(`kind`)도 작업 유형(`type`)도 없다.** 유형은 사람이 먼저 고르고,
 * 작업 유형은 **모델이 아예 안 낸다**(2026-09-16 사용자 지정) — 폼에서 고른다.
 */
export type DraftTicket = {
  title: string;
  body: string;
  priority: TicketPriority | null;
  device: TicketDevice | null;
  environment: TicketEnvironment | null;
  due_date: string | null;
};

export type DraftReply = {
  status: 'ask' | 'ready';
  /** `ask` 면 1~3개. **한 번에 온다** — 하나씩 물으면 그만큼 더 기다린다. */
  questions: DraftQuestion[];
  draft: DraftTicket | null;
  /** 채운 칸의 근거 — 사람이 한 말에서 딴 인용. 화면에 보인다. */
  evidence: string[];
  model: string;
  /** 이 답 뒤에 또 물을 수 있나. **되묻기는 한 번뿐이라 언제나 거짓이다.** */
  can_ask_again: boolean;
};

export type DraftInput = {
  project_id: number;
  /** **사람이 고른다.** 대화 전에 정해진다. */
  kind: TicketKind;
  /** 묻고 받은 것 전부. 라운드는 **이 수로 판정된다.** */
  answers: DraftAnswer[];
};

export function draftTicket(input: DraftInput): Promise<DraftReply> {
  return request<DraftReply>('/tickets/-/draft', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** 챗봇 입구를 열 수 있나. 못 열면 일반 생성으로 바로 간다. */
export function draftReady(): Promise<{ ready: boolean }> {
  return request<{ ready: boolean }>('/tickets/-/draft-ready');
}

export class NotLoggedInError extends Error {
  constructor() {
    super('로그인 안 됨');
    this.name = 'NotLoggedInError';
  }
}

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

type FastApiError = { detail?: unknown };

function detailToText(detail: unknown): string | null {
  if (typeof detail === 'string') return detail;
  if (!Array.isArray(detail)) return null;
  const parts = detail.map((item) => {
    if (typeof item === 'object' && item !== null && 'msg' in item) {
      return String((item as { msg: unknown }).msg);
    }
    return JSON.stringify(item);
  });
  return parts.join('; ');
}

async function readError(res: Response): Promise<string> {
  const text = await res.text();
  if (text) {
    try {
      const data = JSON.parse(text) as FastApiError;
      const fromDetail = detailToText(data.detail);
      if (fromDetail) return fromDetail;
    } catch {
      return text;
    }
  }
  return `요청이 실패했다 (HTTP ${res.status})`;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch (err) {
    throw new Error(
      `서버에 닿지 못했다: ${err instanceof Error ? err.message : String(err)}`,
      { cause: err },
    );
  }

  if (res.status === 401) throw new NotLoggedInError();
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

// --- 로그인 -----------------------------------------------------------------

export type AuthConfig = { google: boolean; dev_login: boolean };

export function getAuthConfig(): Promise<AuthConfig> {
  return request<AuthConfig>('/auth/config');
}

export function getMe(): Promise<User> {
  return request<User>('/auth/me');
}

/**
 * 내 프로필 사진을 바꾼다. **줄이는 것은 화면이 한다** — `avatarDataUrl`.
 *
 * 서버가 바이트를 보고 형식을 정하고 256KB 를 넘으면 거절한다.
 */
export function putMyAvatar(dataUrl: string): Promise<User> {
  return request<User>('/auth/me/avatar', {
    method: 'PUT',
    body: JSON.stringify({ data_url: dataUrl }),
  });
}

/** 사진을 지운다. 지우면 이니셜로 돌아간다. */
export function deleteMyAvatar(): Promise<User> {
  return request<User>('/auth/me/avatar', { method: 'DELETE' });
}

export function updateMe(nickname: string | null): Promise<User> {
  return request<User>('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify({ nickname }),
  });
}

export function devLogin(): Promise<User> {
  return request<User>('/auth/dev-login', { method: 'POST' });
}

export function logout(): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/auth/logout', { method: 'POST' });
}

// --- 사람과 등급 -------------------------------------------------------------

/** **관리자만 부를 수 있다.** 목록에 이메일이 실려서다. */
export function listUsers(): Promise<MemberList> {
  return request<MemberList>('/users');
}

export function setUserRole(id: number, role: Role): Promise<Member> {
  return request<Member>(`/users/${id}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
}

/**
 * **가진 권한 전부**를 보낸다 — 더하기·빼기가 아니다.
 *
 * 체크박스가 보내는 것은 「지금 체크된 것」이고, 그것을 그대로 저장하면
 * 화면과 표가 언제나 같다. 자기 것은 못 고친다(409).
 */
export function setUserPermissions(id: number, permissions: string[]): Promise<Member> {
  return request<Member>(`/users/${id}/permissions`, {
    method: 'PUT',
    body: JSON.stringify({ permissions }),
  });
}

/**
 * 계정을 끄고 켠다. **지우지 않는다** — 만든 티켓과 배포 기록이 남아야 한다.
 *
 * ⚠ 자기 자신과 관리자는 못 끈다(409). 끄면 되살릴 사람이 없어진다.
 */
export function setUserActive(id: number, isActive: boolean): Promise<Member> {
  return request<Member>(`/users/${id}/active`, {
    method: 'PATCH',
    body: JSON.stringify({ is_active: isActive }),
  });
}

// --- 프로젝트 ---------------------------------------------------------------

export function listProjects(): Promise<ProjectList> {
  return request<ProjectList>('/projects');
}

/** **관리자만 만든다**(2026-08-26). 역할과 무관하게 403 이다. */
export function createProject(input: { key: string; name: string }): Promise<Project> {
  return request<Project>('/projects', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** **준 것만 바꾼다.** 이름만 고칠 때 키를 함께 보내면 옛 값으로 덮어쓴다. */
export function updateProject(
  id: number,
  input: { key?: string; name?: string },
): Promise<Project> {
  return request<Project>(`/projects/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** **빈 프로젝트만 지워진다.** 티켓이 하나라도 있으면 409 다. */
export function deleteProject(id: number): Promise<void> {
  return request<void>(`/projects/${id}`, { method: 'DELETE' });
}

// --- 라벨 -------------------------------------------------------------------

export function listLabels(projectId: number): Promise<LabelList> {
  return request<LabelList>(`/labels${query({ project_id: projectId })}`);
}

export function createLabel(input: {
  project_id: number;
  name: string;
  color: LabelColor;
}): Promise<Label> {
  return request<Label>('/labels', { method: 'POST', body: JSON.stringify(input) });
}

/**
 * 이름과 색을 고친다.
 *
 * **지웠다 다시 만드는 것과 다르다.** 지우면 붙어 있던 티켓에서 라벨이 다
 * 떨어진다(`ticket_labels` 가 CASCADE) — 여기서는 id 가 그대로다.
 */
export function updateLabel(
  id: number,
  input: { name?: string; color?: LabelColor },
): Promise<Label> {
  return request<Label>(`/labels/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteLabel(id: number): Promise<void> {
  return request<void>(`/labels/${id}`, { method: 'DELETE' });
}

// --- 티켓 -------------------------------------------------------------------

export type TicketFilter = {
  assignee_id?: number | null;
  reporter_id?: number | null;
  type?: TicketType | null;
  priority?: TicketPriority | null;
  label_id?: number | null;
  q?: string | null;
  /** 유형·디바이스·환경(2026-08-26). 「이슈만 보기」가 이 셋 중 첫째다. */
  kind?: TicketKind | null;
  device?: TicketDevice | null;
  environment?: TicketEnvironment | null;
  /**
   * 보류(2026-09-02). **안 보내면 전부 준다** — 「기본은 숨긴다」로 두면
   * 미뤄 둔 티켓이 화면 어디에도 안 보여 되돌릴 길을 못 찾는다.
   */
  held?: boolean | null;
};

/**
 * 필터를 쿼리 문자열로 옮긴다. **`held` 만 따로 다루는 이유** — `query` 가
 * 문자열·숫자만 받는데 이 축은 불리언이고, `false` 를 그냥 넘기면 「안
 * 보냄」과 구별이 안 된다(빈 값으로 떨어진다).
 */
function toQuery(filter: TicketFilter): Record<string, string | number | null | undefined> {
  const { held, ...rest } = filter;
  return { ...rest, held: held == null ? undefined : String(held) };
}

export function listTickets(
  projectId: number,
  filter: TicketFilter = {},
): Promise<TicketList> {
  return request<TicketList>(
    `/tickets${query({ project_id: projectId, ...toQuery(filter) })}`,
  );
}

export function getTicketMeta(): Promise<TicketMeta> {
  return request<TicketMeta>('/tickets/meta');
}

export function listMembers(): Promise<{ members: UserBrief[] }> {
  return request<{ members: UserBrief[] }>('/tickets/-/members');
}

export function getTicket(id: number): Promise<TicketDetail> {
  return request<TicketDetail>(`/tickets/${id}`);
}

export type TicketCreateInput = {
  project_id: number;
  title: string;
  body?: string;
  col?: TicketCol;
  /** **만들 때만 정한다.** 고치는 API 는 이 칸을 안 받는다. */
  kind?: TicketKind;
  device?: TicketDevice | null;
  environment?: TicketEnvironment | null;
  type?: TicketType | null;
  priority?: TicketPriority | null;
  assignee_id?: number | null;
  due_date?: string | null;
  label_ids?: number[];
};

export function createTicket(input: TicketCreateInput): Promise<TicketCard> {
  return request<TicketCard>('/tickets', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * **보낸 칸만 고친다.** `null` 은 「비운다」이고, 안 보내면 그대로 둔다 —
 * 담당자 해제와 「제목만 고치기」가 이 구분 없이는 같은 요청이 된다.
 */
export type TicketUpdateInput = {
  expected_updated_at?: string;
  title?: string;
  body?: string;
  /** 다른 프로젝트로 옮긴다. **라벨이 함께 비워진다**(프로젝트마다 따로 산다). */
  project_id?: number;
  /** 이슈에만 보낼 수 있다. 할 일에 보내면 400 이다. */
  device?: TicketDevice | null;
  environment?: TicketEnvironment | null;
  type?: TicketType | null;
  priority?: TicketPriority | null;
  assignee_id?: number | null;
  due_date?: string | null;
  label_ids?: number[];
};

export function updateTicket(
  id: number,
  input: TicketUpdateInput,
): Promise<TicketCard> {
  return request<TicketCard>(`/tickets/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/**
 * 칸과 자리를 함께 바꾼다. **이웃 둘을 보낸다** — 인덱스를 보내면 그 사이에
 * 남이 카드를 하나 넣은 순간 엉뚱한 자리로 간다.
 */
export function moveTicket(
  id: number,
  input: {
    expected_updated_at?: string;
    col: TicketCol;
    after_id?: number | null;
    before_id?: number | null;
    /** 「작업 중」으로 옮길 때만 쓴다. 다른 칸에서는 서버가 무시한다. */
    estimate_days?: number | null;
  },
): Promise<TicketCard> {
  return request<TicketCard>(`/tickets/${id}/move`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function deleteTicket(id: number): Promise<void> {
  return request<void>(`/tickets/${id}`, { method: 'DELETE' });
}

/* ── 알림 (2026-09-01) ─────────────────────────────────────────
   인앱 알림함이다 — 외부 채널은 확정 사항 밖. 받을 사람은 서버가
   사건마다 파생한다(생성자·작업자, 새 티켓은 개발). 구독 화면이 없다.

   타입 이름이 `Notification` 이 아니라 `Notice` 인 이유: DOM 전역에
   `Notification`(브라우저 푸시)이 이미 있어서, 같은 이름을 쓰면 import
   를 빠뜨린 자리가 **조용히 전역 타입으로 컴파일된다.** ── */

/**
 * 옛 배포 알림이 싣던 환경. **배포 기능은 2026-10-18 에 걷었다** — 그 전에
 * 쌓인 알림을 읽을 때만 쓴다.
 */
export type DeployEnvironment = 'staging' | 'production';

/** 옛 QA 알림이 싣던 상태. 위와 같은 이유로 남겨 둔다. */
type LegacyQaStatus = 'not_required' | 'pending' | 'passed' | 'failed';

export type NoticeKind =
  | 'ticket.created'
  | 'ticket.assigned'
  | 'ticket.qa_changed'
  | 'ticket.moved'
  | 'ticket.commented'
  /* 2026-09-02 에 셋이 늘었다. 늘리는 기준은 **사람이 기다리고 있는
     사건인가** 하나다 — 「무슨 일이 일어났다」를 다 넣으면 아무도 안 본다. */
  | 'ticket.deploy_recorded'
  | 'deploy.interrupted'
  | 'ticket.deployed'
  | 'deploy.failed'
  | 'ticket.mentioned';

export type Notice = {
  id: number;
  kind: NoticeKind;
  ticket_id: number;
  /** `PLUS-118`. **사건이 난 순간의 스냅숏이다** — 지금 제목과 다를 수 있다. */
  ticket_key: string;
  ticket_title: string;
  /**
   * **어디로 갔나.** 종류마다 읽는 축이 다르다 —
   * `ticket.moved` 는 업무 칸, `ticket.deployed`·`deploy.failed` 는
   * 환경(`staging`·`production`)이다. 나머지 종류는 `null`.
   */
  col: TicketCol | DeployEnvironment | LegacyQaStatus | null;
  /** 한 사람. 계정이 지워졌으면 이 스냅숏만 남는다. */
  actor_name: string | null;
  created_at: string;
  /** `null` 이 「안 읽음」이다. */
  read_at: string | null;
};

export type NoticeList = {
  items: Notice[];
  /** 안 읽은 **전체** 개수. 목록은 잘려도 이 수는 전체다. */
  unread: number;
};

export function listNotifications(beforeId?: number): Promise<NoticeList> {
  return request<NoticeList>(`/notifications${query({ before_id: beforeId })}`);
}

/** 종 배지가 주기적으로 치는 가벼운 경로. 목록을 통째로 나르지 않는다. */
export function unreadNotificationCount(): Promise<{ count: number }> {
  return request<{ count: number }>('/notifications/unread-count');
}

export function readNotification(id: number): Promise<void> {
  return request<void>(`/notifications/${id}/read`, { method: 'POST' });
}

export function readAllNotifications(): Promise<void> {
  return request<void>('/notifications/read-all', { method: 'POST' });
}

/* ── 티켓 댓글 (2026-09-01) ─────────────────────────────────────
   진행 문의·확인 왕복이 티켓 안에서 오간다 — 「댓글은 안 만든다」
   (2026-08-21)를 사용자가 뒤집었다. 읽기·쓰기 모두 `ticket.view` 다
   (서버 판단 — `routers/comments.py` 머리말). ── */

export type TicketComment = {
  id: number;
  ticket_id: number;
  /** 사람 작성자. 계정이 사라졌으면 `null` — 「알 수 없음」으로 그린다. */
  author: UserBrief | null;
  /** 에이전트 작성자(`pm` 꼴). 사람 댓글에서는 `null` 이다. */
  agent_author: string | null;
  /** 마크다운. 지금 화면은 티켓 본문과 같은 규칙(줄바꿈 유지)으로 그린다. */
  body: string;
  created_at: string;
  updated_at: string;
  /** 고쳐졌나. **서버가 판정한다** — 시각 둘을 화면이 견주지 않는다. */
  edited: boolean;
};

export type TicketCommentList = { comments: TicketComment[] };

export function listComments(ticketId: number): Promise<TicketCommentList> {
  return request<TicketCommentList>(`/tickets/${ticketId}/comments`);
}

export function createComment(
  ticketId: number,
  body: string,
): Promise<TicketComment> {
  return request<TicketComment>(`/tickets/${ticketId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

/** 자기 댓글만 된다 — 서버가 작성자를 본다(관리자도 남의 말은 못 고친다). */
export function updateComment(
  ticketId: number,
  commentId: number,
  body: string,
): Promise<TicketComment> {
  return request<TicketComment>(`/tickets/${ticketId}/comments/${commentId}`, {
    method: 'PATCH',
    body: JSON.stringify({ body }),
  });
}

/** 자기 것 또는 관리자. 관리자 지우기는 뒷정리다. */
export function deleteComment(ticketId: number, commentId: number): Promise<void> {
  return request<void>(`/tickets/${ticketId}/comments/${commentId}`, {
    method: 'DELETE',
  });
}

/* ── 보류 (2026-09-02 사용자 확정) ─────────────────────────────
   안 할 요청이 이슈 칸에 영원히 남아 보드를 죽이는 자리를 닫는다.

   **칸과 다른 축이다.** 어느 칸에 있든 미룰 수 있고, 미뤄도 그 칸에
   그대로 있다 — 업무 칸 넷은 확정이라 늘리지 않았다.

   ⚠ 「거절」·「중복」은 없다. 물을 때는 값 셋을 담는 「결론」으로 물었는데
   사용자가 이름을 **보류 하나**로 좁혔다. 지어내지 마라. ── */

export function holdTicket(
  ticketId: number,
  held: boolean,
  reason?: string | null,
  expectedUpdatedAt?: string,
): Promise<TicketCard> {
  return request<TicketCard>(`/tickets/${ticketId}/hold`, {
    method: 'POST',
    body: JSON.stringify({ held, reason: reason ?? null, expected_updated_at: expectedUpdatedAt }),
  });
}

/* ── 티켓 변경 줄 (2026-09-02) ─────────────────────────────────
   창업 문장 「히스토리를 못 찾겠다」의 본체다. 우선순위를 누가 언제
   낮췄는지, 작업자가 언제 바뀌었는지가 그전에는 어디에도 안 남았다 —
   남는 것은 마지막 값 하나뿐이었다.

   ⚠ **감사 층이 아니다.** 티켓 하나에 붙는 변경 줄이고, 화면이 댓글과
   같은 자리에서 읽는다. ── */

export type TicketEventField =
  | 'qa'
  | 'requires_deploy'
  | 'col'
  | 'title'
  | 'body'
  | 'type'
  | 'priority'
  | 'assignee'
  | 'due_date'
  | 'estimate_days'
  | 'held'
  | 'deploy_col'
  | 'project';

export type TicketEvent = {
  id: number;
  field: TicketEventField;
  /**
   * **API 가 그 칸을 표현하는 그대로다.** 코드가 있는 칸(`col`·`type`·
   * `priority`·`deploy_col`)은 코드이고 화면이 그 말을 아는 표를 이미
   * 갖고 있다(`columns.ts`) — 서버가 한국어로 옮기면 표가 두 벌이 된다.
   *
   * 작업자는 **그때의 표시 이름**이다(계정이 지워져도 남아야 한다).
   * 본문은 둘 다 `null` — 「바뀌었다」만 남긴다.
   */
  from_text: string | null;
  to_text: string | null;
  /** 누가. **`null` 이면 사람이 아니다** — 웹훅이 PR 병합으로 옮긴 것. */
  actor_name: string | null;
  created_at: string;
};

/** 일어난 순서대로 온다(`id` 순 — 같은 수정의 여러 줄은 시각이 같다). */
export function listTicketEvents(ticketId: number): Promise<{ events: TicketEvent[] }> {
  return request<{ events: TicketEvent[] }>(`/tickets/${ticketId}/events`);
}



export type TrashKind = 'tickets';
export type TrashItem = { id: number; title: string; project_id: number; deleted_at: string };
export function listTrash(kind: TrashKind, projectId: number | null, beforeId?: number): Promise<TrashItem[]> {
  return request(`/trash/${kind}${query({ project_id: projectId, before_id: beforeId })}`);
}
export function restoreTrash(kind: TrashKind, id: number): Promise<void> {
  return request(`/trash/${kind}/${id}/restore`, { method: 'POST' });
}

export type TicketRevision = { id: number; body: string; actor_name: string | null; created_at: string };
export function listTicketRevisions(id: number, beforeId?: number): Promise<TicketRevision[]> {
  return request(`/tickets/${id}/revisions${query({ before_id: beforeId })}`);
}
export function restoreTicketBody(id: number, revisionId: number, updatedAt: string): Promise<TicketCard> {
  return request(`/tickets/${id}/revisions/${revisionId}/restore`, {
    method: 'POST', body: JSON.stringify({ expected_updated_at: updatedAt }),
  });
}
